import { useRef, useState, useEffect, useCallback } from 'react'
import { useFillCaseImages, useSetToast } from 'hooks'
import { useActions } from '@/hooks/editor/useActions'
import { caseAtom, itemsLockedAtom, pageLoadingAtom } from 'atoms'
import { caseImagesAtom } from '@/atoms/caseImageAtoms'
import { getCaseImageList } from '@/api/caseImages'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import { DesignAssetModal, FileUploadHandler, ToolbarIcon } from './'
import { FullscreenModal } from 'components'
import Flex from 'components/priority/Flex/Flex'
import DesignerGate from 'components/auth/DesignerGate'
import Divider from 'components/global/Divider/Divider'
import { useTranslation } from 'react-i18next'
import { isBulkProcessingAtom } from '@/atoms/_editorAtoms'
import { useCaseImageUpload } from '@/hooks/useCaseImageUpload'
import { useIsAdmin } from '@/hooks/utility/useIsAdmin'

const useToolbarActions = (action) => {
  return {
    addText: useCallback(() => action.insertText(), [action]),
    addShape: useCallback(() => action.insertShape(), [action]),
    addQRCode: useCallback(() => action.insertQRCode(), [action]),
    addLine: useCallback(() => action.insertLine(), [action]),
    zoomOut: useCallback(() => action.zoomOut(), [action]),
    zoomIn: useCallback(() => action.zoomIn(), [action]),
    previousPage: useCallback(() => action.previousPage(), [action]),
    nextPage: useCallback(() => action.nextPage(), [action]),
  }
}

// Components
const ToolbarButton = ({
  icon,
  tooltip,
  onClick,
  disabled,
  'data-testid': testId,
}) => (
  <ToolbarIcon
    icon={icon}
    tooltip={tooltip}
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
  />
)

// Main component
export const EditorToolbar = ({ setShowSlideout }) => {
  const { t } = useTranslation()
  const setToast = useSetToast()
  const inputFile = useRef(null)
  const action = useActions()
  const _case = useRecoilValue(caseAtom)
  const setCaseImages = useSetRecoilState(caseImagesAtom)
  const [pageLoading, setPageLoading] = useRecoilState(pageLoadingAtom)
  const itemsLocked = useRecoilValue(itemsLockedAtom)
  const isBulkProcessing = useRecoilValue(isBulkProcessingAtom)
  const isAdmin = useIsAdmin()
  const fillCaseImages = useFillCaseImages()
  const [newImage, setNewImage] = useState(null)
  const [showDesignAssetModal, setShowDesignAssetModal] = useState(false)

  const { uploadImage } = useCaseImageUpload({
    caseId: _case?.id,
    isAdmin,
    componentName: 'EditorToolbar',
  })

  const handleCaseImageUpload = useCallback(
    async (e) => {
      const { files } = e?.target || {}
      if (!files || !files.length) return

      const file = files[0]
      setPageLoading(true)

      let result = null
      try {
        result = await uploadImage(file)
      } catch (error) {
        console.error('Image upload failed:', error)
        setToast({ text: error.message || t('toast.imageUploadFailed') })
        setPageLoading(false)
        return
      }

      try {
        const images = await getCaseImageList(_case.id)
        setCaseImages(images)
        fillCaseImages()

        setNewImage({
          caseImage: images.length - 1,
          url: result.imageUrl,
        })
      } catch (error) {
        console.error('Case sync failed after upload:', error)
        setToast({ text: t('toast.imageUploadedButSyncFailed') })
      } finally {
        setPageLoading(false)
      }
    },
    [
      _case,
      uploadImage,
      setCaseImages,
      fillCaseImages,
      setPageLoading,
      setToast,
      t,
    ],
  )

  const toolbarActions = useToolbarActions(action)

  useEffect(() => {
    if (!newImage) return

    if (!itemsLocked) {
      action.insertImage(newImage)
    } else {
      setToast({
        icon: 'check',
        text: t('toast.imageNotInsertedFastTrack'),
      })
    }

    setNewImage(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_case?.filledImages, action, newImage])

  const designAssetModalProps = {
    setShowDesignAssetModal,
  }

  return (
    <>
      <Flex
        center
        padding="0 12px"
        position="absolute"
        bottom="24px"
        height="48px"
        radius="4px"
        background="lockedAlmostBlack"
        pointerEvents={isBulkProcessing ? 'none' : 'auto'}
        data-editor-ui="editor-toolbar"
      >
        <FileUploadHandler
          inputRef={inputFile}
          onChange={handleCaseImageUpload}
        >
          <ToolbarButton
            icon="editorImage"
            tooltip={t('editor.toolbar.imageTooltip')}
            onClick={() => inputFile.current.click()}
            data-testid="editor-toolbar-upload-image"
          />
        </FileUploadHandler>

        <DesignerGate>
          <ToolbarButton
            icon="designAssets"
            tooltip={t('editor.toolbar.assetTooltip')}
            onClick={() => setShowDesignAssetModal(true)}
            data-testid="editor-toolbar-add-design-asset"
          />
        </DesignerGate>

        <ToolbarButton
          icon="editorText"
          tooltip={t('editor.toolbar.textTooltip')}
          onClick={toolbarActions.addText}
          disabled={itemsLocked}
          data-testid="editor-toolbar-add-text"
        />
        <ToolbarButton
          icon="editorShape"
          tooltip={t('editor.toolbar.shapeTooltip')}
          onClick={toolbarActions.addShape}
          disabled={itemsLocked}
          data-testid="editor-toolbar-add-shape"
        />
        <ToolbarButton
          icon="qrCode"
          tooltip={t('editor.toolbar.qrCodeTooltip')}
          onClick={toolbarActions.addQRCode}
          disabled={itemsLocked}
          data-testid="editor-toolbar-add-qr-code"
        />
        <ToolbarButton
          icon="editorLine"
          tooltip={t('editor.toolbar.lineTooltip')}
          onClick={toolbarActions.addLine}
          disabled={itemsLocked}
          data-testid="editor-toolbar-add-line"
        />

        <Divider vertical fill="lockedGray2" margin="0 6px" />

        <ToolbarButton
          icon="editorZoomOut"
          tooltip={t('editor.toolbar.zoomOutTooltip')}
          onClick={toolbarActions.zoomOut}
          data-testid="editor-toolbar-zoom-out"
        />
        <ToolbarButton
          icon="editorZoomIn"
          tooltip={t('editor.toolbar.zoomInTooltip')}
          onClick={toolbarActions.zoomIn}
          data-testid="editor-toolbar-zoom-in"
        />

        <Divider vertical fill="lockedGray2" margin="0 6px" />

        <ToolbarButton
          icon="editorPreviousPage"
          tooltip={t('editor.toolbar.previousPageTooltip')}
          onClick={toolbarActions.previousPage}
          disabled={pageLoading}
          data-testid="editor-toolbar-previous-page"
        />
        <ToolbarButton
          icon="editorShuffler"
          tooltip={t('editor.toolbar.shufflePagesTooltip')}
          onClick={() => setShowSlideout(true)}
          disabled={pageLoading}
          data-testid="editor-toolbar-pages"
        />
        <ToolbarButton
          icon="editorNextPage"
          tooltip={t('editor.toolbar.nextPageTooltip')}
          onClick={toolbarActions.nextPage}
          disabled={pageLoading}
          data-testid="editor-toolbar-next-page"
        />
      </Flex>
      <FullscreenModal show={[showDesignAssetModal, setShowDesignAssetModal]}>
        <DesignAssetModal {...designAssetModalProps} />
      </FullscreenModal>
    </>
  )
}

export default EditorToolbar
